import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaTrash, FaChevronDown, FaChevronUp, FaEdit, FaTruckLoading, FaHistory, FaFilePdf, FaHandshake, FaCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import saleService from '../services/saleService';
import paymentPromiseService from '../services/paymentPromiseService';
import { generateDeliveryNotePDF } from '../utils/pdfGenerator';
import api from '../services/api';

const fmtCur = (n) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(n || 0);

const SalesPage = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [selectedSaleForHistory, setSelectedSaleForHistory] = useState(null);
  const [settings, setSettings] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'sale_date', direction: 'desc' });

  // Ödeme sözü modal state
  const [promiseModal, setPromiseModal] = useState({ open: false, sale: null });
  const [promises, setPromises] = useState([]);
  const [promiseForm, setPromiseForm] = useState({ promise_date: '', amount: '', notes: '' });
  const [promiseLoading, setPromiseLoading] = useState(false);


  const fetchSales = async () => {
    try {
      const data = await saleService.getAll();
      setSales(data);
    } catch (error) {
      toast.error('Satışlar yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSales();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings');
      setSettings(res.data);
    } catch (e) {
      console.error("Ayarlar çekilemedi");
    }
  };

  const toggleRow = (id) => setExpandedRow(expandedRow === id ? null : id);

  const handleDelete = async (id) => {
    if (window.confirm('Bu satışı silmek istediğinize emin misiniz? Stoklar geri yüklenecek ve cari hareket silinecektir.')) {
      try {
        await saleService.delete(id);
        toast.success('Satış silindi.');
        fetchSales();
      } catch (error) {
        toast.error('Silme işlemi başarısız.');
      }
    }
  };

  const filteredSales = sales.filter(sale => 
    sale.company_name?.toLocaleLowerCase('tr-TR').includes(searchTerm.toLocaleLowerCase('tr-TR'))
  );

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? <FaChevronUp className="ms-1 small" /> : <FaChevronDown className="ms-1 small" />;
  };

  const sortedSales = React.useMemo(() => {
    let sortableItems = [...filteredSales];
    sortableItems.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      const getSafeItems = (s) => {
        let it = [];
        try {
          if (Array.isArray(s.items)) it = s.items;
          else if (typeof s.items === 'string') {
            it = JSON.parse(s.items);
            if (typeof it === 'string') it = JSON.parse(it);
          }
        } catch (e) { it = []; }
        return Array.isArray(it) ? it : [];
      };

      const aTotalQty = getSafeItems(a).reduce((acc, item) => acc + (Number(item.quantity) || 0), 0);
      const bTotalQty = getSafeItems(b).reduce((acc, item) => acc + (Number(item.quantity) || 0), 0);

      if (sortConfig.key === 'totalQty') {
        aVal = aTotalQty;
        bVal = bTotalQty;
      } else if (sortConfig.key === 'remainingQty') {
        aVal = aTotalQty - (Number(a.delivered_quantity) || 0);
        bVal = bTotalQty - (Number(b.delivered_quantity) || 0);
      } else if (sortConfig.key === 'subTotal') {
        aVal = (Number(a.total_amount) || 0) / (1 + (Number(a.tax_rate) || 0) / 100);
        bVal = (Number(b.total_amount) || 0) / (1 + (Number(b.tax_rate) || 0) / 100);
      } else if (sortConfig.key === 'delivered_quantity' || sortConfig.key === 'total_amount') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else if (sortConfig.key === 'sale_date') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else if (sortConfig.key === 'status') {
        const aRem = aTotalQty - (Number(a.delivered_quantity) || 0);
        const bRem = bTotalQty - (Number(b.delivered_quantity) || 0);
        aVal = aRem <= 0 ? 1 : (Number(a.delivered_quantity) > 0 ? 2 : 3);
        bVal = bRem <= 0 ? 1 : (Number(b.delivered_quantity) > 0 ? 2 : 3);
      } else {
        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sortableItems;
  }, [filteredSales, sortConfig]);



  const openPromiseModal = async (sale) => {
    setPromiseModal({ open: true, sale });
    setPromiseForm({ promise_date: '', amount: '', notes: '' });
    setPromiseLoading(true);
    try {
      const data = await paymentPromiseService.getBySaleId(sale.id);
      setPromises(data);
    } catch { toast.error('Ödeme sözleri yüklenemedi.'); }
    finally { setPromiseLoading(false); }
  };

  const handleAddPromise = async (e) => {
    e.preventDefault();
    if (!promiseForm.promise_date || !promiseForm.amount) {
      toast.warning('Tarih ve tutar zorunludur.');
      return;
    }
    try {
      await paymentPromiseService.create({
        sale_id: promiseModal.sale.id,
        current_account_id: promiseModal.sale.current_account_id,
        promise_date: promiseForm.promise_date,
        amount: parseFloat(promiseForm.amount),
        notes: promiseForm.notes
      });
      toast.success('Ödeme sözü eklendi.');
      setPromiseForm({ promise_date: '', amount: '', notes: '' });
      const data = await paymentPromiseService.getBySaleId(promiseModal.sale.id);
      setPromises(data);
    } catch { toast.error('Eklenemedi.'); }
  };

  const handleMarkPromisePaid = async (id) => {
    try {
      await paymentPromiseService.updateStatus(id, 'odendi');
      const data = await paymentPromiseService.getBySaleId(promiseModal.sale.id);
      setPromises(data);
      toast.success('Ödendi olarak işaretlendi.');
    } catch { toast.error('Güncelleme başarısız.'); }
  };

  const handleDeletePromise = async (id) => {
    try {
      await paymentPromiseService.delete(id);
      const data = await paymentPromiseService.getBySaleId(promiseModal.sale.id);
      setPromises(data);
      toast.success('Silindi.');
    } catch { toast.error('Silme başarısız.'); }
  };

  const openHistoryModal = (data, sale) => {
    const sortedData = [...data].sort((a, b) => new Date(b.date) - new Date(a.date));
    setHistoryData(sortedData);
    setSelectedSaleForHistory(sale);
    setShowHistoryModal(true);
  };

  const handleDeliveryUpdate = async (sale) => {
    let items = [];
    try {
      if (Array.isArray(sale.items)) {
        items = sale.items;
      } else if (typeof sale.items === 'string') {
        items = JSON.parse(sale.items);
        if (typeof items === 'string') {
          items = JSON.parse(items);
        }
      }
    } catch (e) {
      items = [];
    }
    if (!Array.isArray(items)) items = [];

    const totalQty = items.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0);
    const currentDelivered = Number(sale.delivered_quantity) || 0;
    const remaining = totalQty - currentDelivered;

    if (remaining <= 0) {
      toast.info('Bu satışın tüm ürünleri zaten teslim edilmiş.');
      return;
    }

    const input = window.prompt(
      `Toplam Sipariş: ${totalQty}\nTeslim Edilen: ${currentDelivered}\nKalan: ${remaining}\n\nTeslim edilecek miktarı giriniz:`,
      remaining
    );

    if (input !== null) {
      const amount = parseFloat(input);
      if (isNaN(amount) || amount <= 0) {
        toast.warning('Geçersiz miktar.');
        return;
      }
      if (amount > remaining) {
        toast.warning('Kalan miktardan fazla teslimat yapılamaz.');
        return;
      }

      try {
        await saleService.updateDelivery(sale.id, amount);
        toast.success(`${amount} birim teslimat kaydedildi.`);
        fetchSales();
      } catch (error) {
        toast.error('Teslimat kaydedilirken hata oluştu.');
      }
    }
  };

  if (loading) return <div>Yükleniyor...</div>;

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Satışlar</h2>
        <Link to="/sales/new" className="btn btn-primary">
          <FaPlus className="me-2" /> Yeni Satış Yap
        </Link>
      </div>

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Müşteri adı ile ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="table-responsive d-none d-md-block">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('sale_date')}>Tarih {getSortIcon('sale_date')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('company_name')}>Müşteri {getSortIcon('company_name')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('totalQty')}>Sipariş Miktarı {getSortIcon('totalQty')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('delivered_quantity')}>Teslim Edilen {getSortIcon('delivered_quantity')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('remainingQty')}>Kalan {getSortIcon('remainingQty')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('delivered_by_name')}>Teslim Eden {getSortIcon('delivered_by_name')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('created_by_name')}>İşlemi Yapan {getSortIcon('created_by_name')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('subTotal')}>Tutar (KDV Hariç) {getSortIcon('subTotal')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('total_amount')}>Tutar (KDV Dahil) {getSortIcon('total_amount')}</th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('status')}>Durum {getSortIcon('status')}</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {sortedSales.map((sale) => {
                  let items = [];
                  try {
                    if (Array.isArray(sale.items)) {
                      items = sale.items;
                    } else if (typeof sale.items === 'string') {
                      items = JSON.parse(sale.items);
                      if (typeof items === 'string') {
                        items = JSON.parse(items);
                      }
                    }
                  } catch (e) {
                    items = [];
                  }
                  
                  if (!Array.isArray(items)) items = [];

                  let deliveries = [];
                  try {
                    if (Array.isArray(sale.deliveries)) {
                      deliveries = sale.deliveries;
                    } else if (typeof sale.deliveries === 'string') {
                      deliveries = JSON.parse(sale.deliveries);
                      if (typeof deliveries === 'string') {
                        deliveries = JSON.parse(deliveries);
                      }
                    }
                  } catch (e) {
                    deliveries = [];
                  }
                  if (!Array.isArray(deliveries)) deliveries = [];

                  const totalQty = items.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0);
                  const deliveredQty = Number(sale.delivered_quantity) || 0;
                  const remainingQty = totalQty - deliveredQty;
                  
                  const taxRate = Number(sale.tax_rate) || 0;
                  const totalAmount = Number(sale.total_amount) || 0;
                  const subTotal = totalAmount / (1 + taxRate / 100);

                  let statusBadge = <span className="badge bg-secondary">Bekliyor</span>;
                  if (deliveredQty > 0 && remainingQty > 0) {
                    statusBadge = <span className="badge bg-warning text-dark">Kısmi Teslimat</span>;
                  } else if (deliveredQty >= totalQty && totalQty > 0) {
                    statusBadge = <span className="badge bg-success">Teslim Edildi</span>;
                  }

                  return (
                  <tr key={sale.id} className={remainingQty > 0 ? "table-default" : "table-light"}>
                    <td>{new Date(sale.sale_date).toLocaleDateString('tr-TR')}</td>
                    <td>{sale.company_name}</td>
                    <td className="fw-bold">{totalQty}</td>
                    <td className="text-primary">{deliveredQty}</td>
                    <td className="text-danger fw-bold">{remainingQty}</td>
                    <td className="small text-muted">
                      {deliveries.length > 0 ? (
                        <div 
                          className="text-primary" 
                          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }} 
                          onClick={() => openHistoryModal(deliveries, sale)}
                          title="Teslimat geçmişini görüntüle"
                        >
                          <FaHistory />
                          <span>
                            {deliveries.reduce((prev, curr) => (new Date(prev.date) > new Date(curr.date) ? prev : curr)).delivered_by}
                          </span>
                          {deliveries.length > 1 && <span className="badge bg-light text-dark border ms-1">+{deliveries.length - 1}</span>}
                        </div>
                      ) : (
                        sale.delivered_by_name || '-'
                      )}
                    </td>
                    <td className="small text-muted">{sale.created_by_name || '-'}</td>
                    <td>
                      {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(subTotal)}
                    </td>
                    <td className="fw-bold">
                      {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(totalAmount)}
                    </td>
                    <td>{statusBadge}</td>
                    <td>
                      {remainingQty > 0 && (
                        <button onClick={() => handleDeliveryUpdate(sale)} className="btn btn-sm btn-outline-success me-2" title="Teslimat Gir">
                          <FaTruckLoading />
                        </button>
                      )}
                      <button onClick={() => openPromiseModal(sale)} className="btn btn-sm btn-outline-warning me-2" title="Ödeme Sözü">
                        <FaHandshake />
                      </button>
                      <Link to={`/sales/edit/${sale.id}`} className="btn btn-sm btn-outline-primary me-2" title="Düzenle">
                        <FaEdit />
                      </Link>
                      <button onClick={() => handleDelete(sale.id)} className="btn btn-sm btn-outline-danger" title="Sil">
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                );
                })}
                {filteredSales.length === 0 && (
                  <tr>
                    <td colSpan="11" className="text-center">Kayıtlı satış bulunamadı.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-md-none">
            {sortedSales.map((sale) => {
              let items = [];
              try {
                if (Array.isArray(sale.items)) {
                  items = sale.items;
                } else if (typeof sale.items === 'string') {
                  items = JSON.parse(sale.items);
                  if (typeof items === 'string') {
                    items = JSON.parse(items);
                  }
                }
              } catch (e) {
                items = [];
              }
              
              if (!Array.isArray(items)) items = [];

              let deliveries = [];
              try {
                if (Array.isArray(sale.deliveries)) {
                  deliveries = sale.deliveries;
                } else if (typeof sale.deliveries === 'string') {
                  deliveries = JSON.parse(sale.deliveries);
                  if (typeof deliveries === 'string') {
                    deliveries = JSON.parse(deliveries);
                  }
                }
              } catch (e) {
                deliveries = [];
              }
              if (!Array.isArray(deliveries)) deliveries = [];

              const totalQty = items.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0);
              const deliveredQty = Number(sale.delivered_quantity) || 0;
              const remainingQty = totalQty - deliveredQty;

              const taxRate = Number(sale.tax_rate) || 0;
              const totalAmount = Number(sale.total_amount) || 0;
              const subTotal = totalAmount / (1 + taxRate / 100);

              return (
              <div className="card mb-3 shadow-sm" key={sale.id}>
                <div className="card-body" onClick={() => toggleRow(sale.id)} style={{ cursor: 'pointer' }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-0 fw-bold">{sale.company_name}</h6>
                      <small className="text-muted">{new Date(sale.sale_date).toLocaleDateString('tr-TR')}</small>
                    </div>
                    <div className="text-end">
                      <div className="fw-bold">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(totalAmount)}</div>
                      <div className="text-muted mt-1">{expandedRow === sale.id ? <FaChevronUp /> : <FaChevronDown />}</div>
                    </div>
                  </div>

                  {expandedRow === sale.id && (
                    <div className="mt-3 border-top pt-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span><strong>Sipariş:</strong> {totalQty}</span>
                        <span className="text-danger"><strong>Kalan:</strong> {remainingQty}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span><strong>KDV Hariç:</strong> {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(subTotal)}</span>
                        <span><strong>KDV Oranı:</strong> %{taxRate}</span>
                      </div>
                      
                      {deliveries.length > 0 ? (
                        <div className="mb-2 bg-light p-2 rounded">
                          <div className="d-flex justify-content-between align-items-center">
                            <strong className="small">Teslimat Geçmişi:</strong>
                            <button className="btn btn-sm btn-link p-0 text-decoration-none" onClick={(e) => { e.stopPropagation(); openHistoryModal(deliveries, sale); }}>
                              <FaHistory className="me-1" /> Detayları Gör ({deliveries.length})
                            </button>
                          </div>
                          <div className="small mt-1 text-muted">
                            Son: {deliveries.reduce((prev, curr) => (new Date(prev.date) > new Date(curr.date) ? prev : curr)).delivered_by} 
                            <span className="ms-1">({deliveries.reduce((prev, curr) => (new Date(prev.date) > new Date(curr.date) ? prev : curr)).quantity})</span>
                          </div>
                        </div>
                      ) : (
                        sale.delivered_by_name && <p className="mb-1 small text-muted"><strong>Son Teslim Eden:</strong> {sale.delivered_by_name}</p>
                      )}

                      <p className="mb-1"><strong>Notlar:</strong> {sale.notes || '-'}</p>
                      <div className="d-flex justify-content-end gap-2 mt-3 flex-wrap">
                        {remainingQty > 0 && (
                          <button onClick={(e) => { e.stopPropagation(); handleDeliveryUpdate(sale); }} className="btn btn-sm btn-outline-success"><FaTruckLoading /> Teslimat</button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); openPromiseModal(sale); }} className="btn btn-sm btn-outline-warning"><FaHandshake /> Ödeme Sözü</button>
                        <Link to={`/sales/edit/${sale.id}`} className="btn btn-sm btn-outline-primary">
                          <FaEdit /> Düzenle
                        </Link>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(sale.id); }} className="btn btn-sm btn-outline-danger">
                          <FaTrash /> Sil
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
            })}
          </div>
        </div>
      </div>

      {/* Ödeme Sözü Modal */}
      {promiseModal.open && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><FaHandshake className="me-2 text-warning" />Ödeme Sözleri — {promiseModal.sale?.company_name}</h5>
                <button className="btn-close" onClick={() => setPromiseModal({ open: false, sale: null })} />
              </div>
              <div className="modal-body">
                {/* Yeni söz formu */}
                <form onSubmit={handleAddPromise} className="card bg-light border-0 p-3 mb-4">
                  <h6 className="fw-semibold mb-3"><FaPlus className="me-1" />Yeni Ödeme Sözü Ekle</h6>
                  <div className="row g-2">
                    <div className="col-sm-4">
                      <label className="form-label small fw-semibold">Tarih *</label>
                      <input type="date" className="form-control form-control-sm" value={promiseForm.promise_date}
                        onChange={e => setPromiseForm(f => ({ ...f, promise_date: e.target.value }))} required />
                    </div>
                    <div className="col-sm-4">
                      <label className="form-label small fw-semibold">Tutar (₺) *</label>
                      <input type="number" step="0.01" min="0.01" className="form-control form-control-sm" placeholder="0.00"
                        value={promiseForm.amount} onChange={e => setPromiseForm(f => ({ ...f, amount: e.target.value }))} required />
                    </div>
                    <div className="col-sm-4">
                      <label className="form-label small fw-semibold">Not</label>
                      <input type="text" className="form-control form-control-sm" placeholder="İsteğe bağlı..."
                        value={promiseForm.notes} onChange={e => setPromiseForm(f => ({ ...f, notes: e.target.value }))} />
                    </div>
                  </div>
                  <div className="mt-2 text-end">
                    <button type="submit" className="btn btn-sm btn-warning fw-semibold px-4">Ekle</button>
                  </div>
                </form>

                {/* Mevcut sözler */}
                <h6 className="fw-semibold mb-2">Mevcut Ödeme Sözleri</h6>
                {promiseLoading ? (
                  <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary" /></div>
                ) : promises.length === 0 ? (
                  <p className="text-muted text-center py-3">Henüz ödeme sözü eklenmemiş.</p>
                ) : (
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr><th>Tarih</th><th className="text-end">Tutar</th><th>Durum</th><th>Not</th><th className="text-center">İşlem</th></tr>
                    </thead>
                    <tbody>
                      {promises.map(p => {
                        const today = new Date().toISOString().split('T')[0];
                        const d = String(p.promise_date).split('T')[0];
                        const isOverdue = p.status === 'gecikti' || (p.status === 'bekliyor' && d < today);
                        return (
                          <tr key={p.id} className={isOverdue ? 'table-danger' : d === today ? 'table-warning' : ''}>
                            <td>{new Date(d + 'T12:00:00').toLocaleDateString('tr-TR')}</td>
                            <td className="text-end fw-bold">{fmtCur(p.amount)}</td>
                            <td>
                              <span className={`badge ${p.status === 'odendi' ? 'bg-success' : p.status === 'gecikti' || isOverdue ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                {p.status === 'odendi' ? 'Ödendi' : p.status === 'gecikti' || isOverdue ? 'Gecikti' : 'Bekliyor'}
                              </span>
                            </td>
                            <td className="text-muted small">{p.notes || '-'}</td>
                            <td className="text-center">
                              {p.status !== 'odendi' && (
                                <button className="btn btn-sm btn-success me-1" title="Ödendi işaretle" onClick={() => handleMarkPromisePaid(p.id)}>
                                  <FaCheck />
                                </button>
                              )}
                              <button className="btn btn-sm btn-outline-danger" title="Sil" onClick={() => handleDeletePromise(p.id)}>
                                <FaTrash />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setPromiseModal({ open: false, sale: null })}>Kapat</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showHistoryModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Teslimat Geçmişi ve Fişler</h5>
                <button type="button" className="btn-close" onClick={() => setShowHistoryModal(false)}></button>
              </div>
              <div className="modal-body p-0">
                <table className="table table-striped mb-0">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>Tarih</th>
                      <th>Teslim Eden</th>
                      <th className="text-end">Miktar</th>
                      <th className="text-center">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.map((d, i) => (
                      <tr key={i}>
                        <td>{new Date(d.date).toLocaleString('tr-TR')}</td>
                        <td>{d.delivered_by}</td>
                        <td className="text-end fw-bold">{d.quantity}</td>
                        <td className="text-center">
                          <button 
                            className="btn btn-sm btn-outline-danger" 
                            onClick={() => generateDeliveryNotePDF(selectedSaleForHistory, d, settings)}
                            title="Teslim Fişi Yazdır">
                            <FaFilePdf />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowHistoryModal(false)}>Kapat</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesPage;
